import { AgxAvatar, AgxSelectChip, AgxSpacer, SelectChipOption } from '@urbanx/agx.ui-components2';
import TasksIcon from 'components/sidebar/icons/tasks-icon';
import { ContentLayout, PageLayout } from 'layout';
import { isEqual } from 'lodash-es';
import { useGetAgencyOptions } from 'pages/allTasks/TaskFilters/useGetAgencyOptions';
import { useGetAssigneeOptions } from 'pages/allTasks/TaskFilters/useGetAssigneeOptions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TodaysTaskStats from './Components/TodaysTaskStats/TodaysTaskStats';
import TodaysTasks from './Components/TodaysTasks/TodaysTasks';
import { DashboardStats } from './Components/DashboardStats/DashboardStats';
import { useGetDashboardStats } from '@urbanx/agx.activities.client';
import { TasksSearchFilterType } from './Components/TasksModal/TasksSearchFilterType';
import { useGetAllAgencies } from '@urbanx/agx.agencies.client';
import TasksModal from './Components/TasksModal/TasksModal';
import {
  createInitialDashboardFilterState,
  storeDashboardFilterState,
} from './tasksDashboardFilters';
import './TasksDashboardPage.scss';

const initialDashboardFilterState = createInitialDashboardFilterState();

const TasksDashboardPage = () => {
  const [assignedToUserIds, setAssignedToUserIds] = useState<string[] | undefined>(
    initialDashboardFilterState.assignedToUserIds
  );
  const [selectedAgencies, setSelectedAgencies] = useState<string[] | undefined>(
    initialDashboardFilterState.agencyIds
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchFilterType, setSearchFilterType] = useState<TasksSearchFilterType>(
    TasksSearchFilterType.Launches
  );

  const { assigneeOptions, assigneeOptionsPending } = useGetAssigneeOptions(assignedToUserIds);

  useEffect(() => {
    storeDashboardFilterState({
      agencyIds: selectedAgencies,
      assignedToUserIds: assignedToUserIds,
    });
  }, [assignedToUserIds, selectedAgencies]);

  useEffect(() => {
    if (assigneeOptionsPending) return;
    if (!assignedToUserIds) return;
    if (assigneeOptions.length <= 1) return;
    if (assignedToUserIds.every((id) => assigneeOptions.find((opt) => opt.value === id))) return;

    const filteredAssignedToUserIds = assignedToUserIds.filter((id) =>
      assigneeOptions.find((opt) => opt.value === id)
    );
    setAssignedToUserIds(filteredAssignedToUserIds);
  }, [assigneeOptions, assignedToUserIds, assigneeOptionsPending]);

  const { agencyOptions, isAgenciesPending: isAgencyOptionsPending } =
    useGetAgencyOptions(selectedAgencies);

  useEffect(() => {
    if (isAgencyOptionsPending) return;
    if (!selectedAgencies) return;
    if (selectedAgencies.every((id) => agencyOptions.find((opt) => opt.value === id))) return;

    const filteredSelectedAgencies = selectedAgencies.filter((id) =>
      agencyOptions.find((opt) => opt.value === id)
    );
    setSelectedAgencies(filteredSelectedAgencies);
  }, [agencyOptions, selectedAgencies, isAgencyOptionsPending]);

  const { data: agencies, isPending: isAgenciesPending } = useGetAllAgencies();

  const handleAssigneeFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedAssigneeValue = selected.map((option) => option.value as string);

      if (!isEqual(selectedAssigneeValue, assignedToUserIds)) {
        setAssignedToUserIds(selectedAssigneeValue);
      }
    },
    [assignedToUserIds]
  );

  const handleAgencyFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedAgencyValue = selected.map((option) => option.value as string);

      if (!isEqual(selectedAgencyValue, selectedAgencies)) setSelectedAgencies(selectedAgencyValue);
    },
    [selectedAgencies]
  );

  const loading = useMemo(() => {
    return assigneeOptionsPending || isAgencyOptionsPending || isAgenciesPending;
  }, [assigneeOptionsPending, isAgencyOptionsPending, isAgenciesPending]);

  const { data: dashboardStats } = useGetDashboardStats(
    selectedAgencies,
    assignedToUserIds,
    !loading
  );

  const onSearchFilterTypeUpdated = useCallback((filterType: TasksSearchFilterType) => {
    setSearchFilterType(filterType);
    setIsModalOpen(true);
  }, []);

  return (
    <PageLayout
      primaryPageTitle="Dashboard"
      agentName={''}
      pageIcon={
        <AgxAvatar size="lg" type="icon-dark" icon={<TasksIcon width={28} height={28} />} />
      }
      header={
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          contentJustification="space-between"
          fill
          style={{
            borderRadius: 'var(--radius-6xl, 40px)',
            background: 'var(--bg-secondary, #FFF)',
          }}
          padding="md"
          className="tasksDashboardFilters"
        >
          <DashboardStats
            tasksDueToday={dashboardStats?.numberOfIncompleteTasksDueToday}
            tasksOverdue={dashboardStats?.numberOfOverdueTasks}
          />
          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            gap="sm"
            contentJustification="end"
            className="tasksDashboardFilterControls"
            wrap
          >
            <AgxSelectChip
              id={'filterAssignee'}
              variant="chip"
              label={'Assignee'}
              onSelect={handleAssigneeFilterUpdate}
              onReset={handleAssigneeFilterUpdate}
              size={'medium'}
              type={'multi-select'}
              options={assigneeOptions}
              searchPlaceHolder="Search Assignee"
              maxLabels={0}
            />
            <AgxSelectChip
              id={'filterAgency'}
              variant="chip"
              label={'Agency'}
              onSelect={handleAgencyFilterUpdate}
              onReset={handleAgencyFilterUpdate}
              size={'medium'}
              type={'multi-select'}
              align="right"
              searchPlaceHolder="Search agency"
              options={agencyOptions}
              maxLabels={0}
            />
          </AgxSpacer>
        </AgxSpacer>
      }
    >
      <ContentLayout hideBackground>
        <AgxSpacer direction="vertical" gap="lg" fill>
          <TodaysTaskStats
            dashboardStats={dashboardStats}
            onStatTypeSelected={onSearchFilterTypeUpdated}
          />
          <TodaysTasks
            isLoading={loading}
            todaysTasksCount={dashboardStats?.numberOfIncompleteTasksDueToday ?? 0}
            selectedAgencies={selectedAgencies}
            assignedToUserIds={assignedToUserIds}
            assigneeOptions={assigneeOptions}
            agencies={agencies ?? []}
            isAgencyOptionsPending={isAgencyOptionsPending}
            assigneeOptionsPending={assigneeOptionsPending}
          />
        </AgxSpacer>
        <TasksModal
          assignedToUserIds={assignedToUserIds}
          selectedAgencies={selectedAgencies}
          searchFilterType={searchFilterType}
          agencies={agencies ?? []}
          assigneeOptions={assigneeOptions}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      </ContentLayout>
    </PageLayout>
  );
};

export default TasksDashboardPage;
